/* External libs */
import { useCallback, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";
import {
  Anchor,
  Breadcrumbs,
  Button,
  ColorInput,
  Group,
  LoadingOverlay,
  Select,
  Stack,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { zodResolver } from "mantine-form-zod-resolver";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* Constants */
import {
  ADMIN_ORGANIZATION,
  HTML_PAGE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  HTML_PAGE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
  ORGANIZATIONS,
} from "_roles";

/* Types */
import { type HtmlPage } from "htmlpage/types/html-page.model";

/* Hooks */
import { useDebug } from "common/debug/hooks/useDebug";
import { useOrganization } from "common/admin/auth/hooks";
import {
  useAddHTMLPageMutation,
  useUpdateHTMLPageMutation,
} from "htmlpage/api/html-page.api";

/* Contexts */
import {
  HTMLPageFormProvider,
  useHTMLPageForm,
} from "htmlpage/context/html-page.context";
/* Schemas */
import { htmlPageSchema } from "./HTMLPageSchema";

/* Components */
import { AccessRights } from "common/admin/auth/AccessRights";
import Collapsable from "common/admin/ui/Collapsable";
import { ADMIN_PATH } from "_urls";
import FileInput from "common/admin/ui/FileInput";

export function HtmlPageAdminForm({
  htmlPage,
  editMode,
}: {
  htmlPage?: HtmlPage;
  editMode: boolean;
}) {
  const { isDebugMode, log } = useDebug();
  const location = useLocation();
  const navigate = useNavigate();

  const [organization] = useOrganization();

  const [addHTMLPage, addHTMLPageResult] = useAddHTMLPageMutation();
  const [updateHTMLPage, updateHTMLPageResult] = useUpdateHTMLPageMutation();
  const [isLoading, setIsLoading] = useState(false);

  const { isError: isErrorAddHTMLPageResult, error: errorAddHTMLPageResult } =
    addHTMLPageResult;
  const {
    isError: isErrorUpdateHTMLPageResult,
    error: errorUpdateHTMLPageResult,
  } = updateHTMLPageResult;

  useEffect(() => {
    if (isDebugMode) {
      if (isErrorAddHTMLPageResult) {
        log(
          `errorAddHTMLPageResult: ${JSON.stringify(errorAddHTMLPageResult)}`,
        );
      }
      if (isErrorUpdateHTMLPageResult) {
        log(
          `errorUpdateHTMLPageResult: ${JSON.stringify(errorUpdateHTMLPageResult)}`,
        );
      }
    }
  }, [
    isDebugMode,
    log,
    isErrorAddHTMLPageResult,
    isErrorUpdateHTMLPageResult,
    errorAddHTMLPageResult,
    errorUpdateHTMLPageResult,
  ]);

  const save = async () => {
    const isValid = form.validate();
    if (!isValid.hasErrors) {
      const transformedData = form.values;

      console.log(JSON.stringify(transformedData));
      setIsLoading(true);

      const isViewMode = location.pathname.endsWith("/duplicate");

      try {
        if (htmlPage && editMode && !isViewMode) {
          await updateHTMLPage({
            id: htmlPage.id,
            ...transformedData,
          }).unwrap(); // Add .unwrap() for consistency
          log(`Update successful`);
          navigate(`../../`);
        } else {
          const addHTMLPagePayload =
            await addHTMLPage(transformedData).unwrap();
          log(`New HTML page created: ${JSON.stringify(addHTMLPagePayload)}`);
          navigate(`../../`); // Add navigation for add mode
        }
      } catch (error) {
        log(`Error: ${error}`);
        // Consider showing user-facing error message here
      } finally {
        setIsLoading(false); // Ensure loading state is reset
      }
    } else {
      log("Form validation failed", isValid.errors);
    }
  };

  const initialFormValues = useCallback(() => {
    if (htmlPage && editMode) {
      return {
        organization: htmlPage.organization || organization || "",
        entity: htmlPage.entity || "",
        creationTime: new Date(htmlPage.creationTime).toISOString(),
        name: htmlPage.name || "",
        content: htmlPage.content || "",
        section: htmlPage.section || "",
        backGroundImage: htmlPage.backGroundImage || "",
        backGroundColor: htmlPage.backGroundColor || "",
        backGroundPosition: htmlPage.backGroundPosition || "",
      };
    }
    return {
      organization: organization || "",
      entity: "",
      creationTime: new Date().toISOString(),
      name: "",
      content: "",
      section: "",
      backGroundImage: "",
      backGroundColor: "blue",
      backGroundPosition: "left",
    };
  }, [htmlPage, editMode, organization]);

  const form = useHTMLPageForm({
    initialValues: initialFormValues(),
    validate: zodResolver(htmlPageSchema),
  });

  const initialValues = useMemo(() => initialFormValues(), [initialFormValues]);

  const isDirty = useMemo(
    () => !isEqual(initialValues, form.values),
    [initialValues, form.values],
  );

  return (
    <>
      {isLoading ? (
        <div className="h-100 w-100 d-flex align-items-center justify-content-center">
          <div className="spinner-border" role="status">
            <LoadingOverlay visible={true} overlayOpacity={100} />
          </div>
        </div>
      ) : null}

      <HTMLPageFormProvider form={form}>
        <Stack>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align="center" m="xs">
              {htmlPage?.name || "New HTML Page"}
            </Title>
            <Breadcrumbs separator="→">
              <Anchor component={Link} to={`/${ADMIN_PATH}`}>
                Admin
              </Anchor>

              <Anchor component={Link} to={`/${ADMIN_PATH}/htmlpageadmin`}>
                HTML Pages
              </Anchor>
            </Breadcrumbs>
            <Group position={"right"}>
              <Button
                className="bg-button-admin disabled:opacity-75"
                disabled={!isDirty}
                onClick={() => {
                  save();
                }}
              >
                Save
              </Button>
            </Group>
          </div>
          <Collapsable title={htmlPage?.name || "Title"}>
            <Group className="grid grid-cols-2">
              <div>
                {organization === ADMIN_ORGANIZATION && (
                  <AccessRights
                    authorizedOrganizations={
                      HTML_PAGE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      HTML_PAGE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Select
                      sx={{ flex: 1 }}
                      label="Organisation"
                      required
                      data={ORGANIZATIONS}
                      {...form.getInputProps("organization")}
                    />
                  </AccessRights>
                )}
                <TextInput
                  required
                  sx={{ flex: 1 }}
                  label={"Title"}
                  {...form.getInputProps("name")}
                />
              </div>
            </Group>

            <Textarea
              label="Content"
              required
              minRows={6}
              {...form.getInputProps("content")}
            />
          </Collapsable>
          <Collapsable title="Background Settings">
            <FileInput
              label="Background Image"
              required
              {...form.getInputProps("backGroundImage")}
            />
            <Group className="grid grid-cols-2">
              <ColorInput
                label="Background Color"
                placeholder="Select a color"
                required
                {...form.getInputProps("backGroundColor")}
              />
            </Group>
            <Group className="grid grid-cols-2">
              <Select
                label="Background Position"
                placeholder="Select background position"
                data={[
                  { value: "inherit", label: "inherit" },
                  { value: "initial", label: "initial" },
                  { value: "center", label: "center" },
                  { value: "right", label: "right" },
                  { value: "left", label: "left" },
                  { value: "bottom", label: "bottom" },
                  { value: "top", label: "top" },
                  { value: "center bottom", label: "center bottom" },
                  { value: "center center", label: "center center" },
                  { value: "center top", label: "center top" },
                  { value: "right bottom", label: "right bottom" },
                  { value: "right center", label: "right center" },
                  { value: "right top", label: "right top" },
                  { value: "left bottom", label: "left bottom" },
                  { value: "left center", label: "left center" },
                  { value: "left top", label: "left top" },
                ]}
                {...form.getInputProps("backGroundPosition")}
              />
            </Group>
          </Collapsable>
        </Stack>
      </HTMLPageFormProvider>
    </>
  );
}
